import { ReportDefinition, ReportHeaderSnippet } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

const DEFAULT_RESERVATION_CREDIT_LIMIT = 500;
const CASH_PAYMENT_METHOD = 'CA';

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function asNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   const amount = Number(value);
   return Number.isFinite(amount) ? amount : null;
}

function formatCurrency(value: any): string {
   const amount = asNumberOrNull(value);
   if (amount === null) return '';

   const formatted = Math.abs(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return amount < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatAmount(value: any): string {
   const amount = asNumberOrNull(value);
   if (amount === null) return '';

   return Math.abs(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });
}

function getGuestCreditLimitRows(reportJson: any): any[] {
   const parsedReportJson = parseReportJson(reportJson);

   return Array.isArray(parsedReportJson?.GuestCreditLimit)
      ? parsedReportJson.GuestCreditLimit
      : [];
}

function getRowId(row: any): string {
   return (
      asString(row?.TyallsRowId) ||
      asString(row?.tyallsRowId) ||
      `gcl:${asString(row?.ReservationId)}:${asString(
         row?.ConfirmationNumber
      )}:${asString(row?.FolioWindowNo)}`
   );
}

function getReservationGroupKey(row: any): string {
   return (
      asString(row?.ReservationId) ||
      asString(row?.ConfirmationNumber) ||
      `${asString(row?.RoomNumber)}:${asString(row?.Name)}`
   );
}

function getFolioWindowNo(row: any): string {
   return asString(row?.FolioWindowNo);
}

function getFolioWindowSortValue(row: any): number {
   const windowNo = Number(row?.FolioWindowNo);
   return Number.isFinite(windowNo) ? windowNo : 9999;
}

function getPaymentMethod(row: any): string {
   return asString(row?.PaymentMethod).toUpperCase();
}

function isCashPayment(row: any): boolean {
   return getPaymentMethod(row) === CASH_PAYMENT_METHOD;
}

function getPayee(row: any): string {
   return asString(row?.Payee) || asString(row?.RoutingInstructions) || '-';
}

function getBalance(row: any): number | null {
   return asNumberOrNull(row?.Balance);
}

function getCreditLimit(row: any): number | null {
   const creditLimit = asNumberOrNull(row?.CreditLimit);

   if (creditLimit !== null) {
      return creditLimit;
   }

   if (isCashPayment(row)) {
      return 0;
   }

   return DEFAULT_RESERVATION_CREDIT_LIMIT;
}

function getVariance(row: any): number | null {
   const variance = asNumberOrNull(row?.Variance);

   if (variance !== null) {
      return variance;
   }

   const creditLimit = getCreditLimit(row);
   const balance = getBalance(row);

   if (creditLimit === null || balance === null) {
      return null;
   }

   return creditLimit - balance;
}

function getTotalPositiveBalance(rows: any[]): number {
   return rows.reduce((total, row) => {
      const balance = getBalance(row);
      return balance !== null && balance > 0 ? total + balance : total;
   }, 0);
}

function hasCashRisk(rows: any[]): boolean {
   return rows.some((row) => {
      const balance = getBalance(row);
      return isCashPayment(row) && balance !== null && balance > 0;
   });
}

function hasCreditLimitWindow(row: any): boolean {
   const creditLimit = getCreditLimit(row);
   return creditLimit !== null && creditLimit > 0;
}

function hasIndividualCreditLimitExceeded(rows: any[]): boolean {
   return rows.some((row) => {
      const variance = getVariance(row);
      return hasCreditLimitWindow(row) && variance !== null && variance < 0;
   });
}

function isTotalExposureExceeded(rows: any[]): boolean {
   return getTotalPositiveBalance(rows) > DEFAULT_RESERVATION_CREDIT_LIMIT;
}

function getSummaryLimit(rows: any[]): number {
   const hasCreditCardLimit = rows.some((row) => {
      const creditLimit = getCreditLimit(row);
      return creditLimit !== null && creditLimit > 0;
   });

   if (!hasCreditCardLimit && hasCashRisk(rows) && !isTotalExposureExceeded(rows)) {
      return 0;
   }

   return DEFAULT_RESERVATION_CREDIT_LIMIT;
}

function getSummaryVariance(rows: any[]): number {
   return getSummaryLimit(rows) - getTotalPositiveBalance(rows);
}

function getRiskLabels(rows: any[]): string[] {
   const labels: string[] = [];

   if (hasIndividualCreditLimitExceeded(rows)) {
      labels.push('Credit Limit Exceeded');
   } else if (isTotalExposureExceeded(rows)) {
      labels.push('Total Balance Over Limit');
   }

   if (hasCashRisk(rows)) {
      labels.push('Cash Risk');
   }

   return labels;
}

function getAlertDisplay(rows: any[]): string {
   const alerts: string[] = [];

   if (hasIndividualCreditLimitExceeded(rows)) {
      alerts.push('Credit Limit Exceeded');
   } else if (isTotalExposureExceeded(rows)) {
      alerts.push('Total Balance Over Limit');
   }

   if (hasCashRisk(rows)) {
      alerts.push('Cash Risk');
   }

   return alerts.join('\n');
}

function getAlertFilterText(rows: any[]): string {
   return getRiskLabels(rows).join(' | ');
}

function getAlertSortRank(rows: any[]): number {
   if (hasIndividualCreditLimitExceeded(rows)) {
      return 1;
   }

   if (isTotalExposureExceeded(rows)) {
      return 2;
   }

   if (hasCashRisk(rows)) {
      return 3;
   }

   return 99;
}

function getAlertSeverity(rows: any[]): 'high' | 'medium' | 'low' | 'none' {
   if (hasIndividualCreditLimitExceeded(rows)) {
      return 'high';
   }

   if (isTotalExposureExceeded(rows)) {
      return 'medium';
   }

   if (hasCashRisk(rows)) {
      return 'low';
   }

   return 'none';
}

function joinUnique(values: string[], separator = '\n'): string {
   return Array.from(new Set(values.map((x) => x.trim()).filter(Boolean))).join(
      separator
   );
}

function formatWindowPaymentWithBalance(rows: any[]): string {
   return joinUnique(
      rows.map((row) => {
         const windowNo = getFolioWindowNo(row);
         const paymentMethod = getPaymentMethod(row) || '-';
         const balance = formatAmount(getBalance(row));

         return windowNo
            ? `${windowNo}: ${paymentMethod} [${balance}]`
            : `${paymentMethod} [${balance}]`;
      })
   );
}

function formatWindowPayees(rows: any[]): string {
   return joinUnique(
      rows.map((row) => {
         const windowNo = getFolioWindowNo(row);
         const payee = getPayee(row);

         return windowNo ? `${windowNo}: ${payee}` : payee;
      })
   );
}

function groupRowsByReservation(rows: any[]): any[][] {
   const groups = new Map<string, any[]>();

   for (const row of rows) {
      const key = getReservationGroupKey(row);
      if (!key) continue;

      const existing = groups.get(key) ?? [];
      existing.push(row);
      groups.set(key, existing);
   }

   return Array.from(groups.values())
      .map((groupRows) =>
         [...groupRows].sort(
            (a, b) => getFolioWindowSortValue(a) - getFolioWindowSortValue(b)
         )
      )
      .sort((a, b) => {
         const firstA = a[0];
         const firstB = b[0];

         return (
            asString(firstA?.RoomNumber).localeCompare(
               asString(firstB?.RoomNumber)
            ) || asString(firstA?.Name).localeCompare(asString(firstB?.Name))
         );
      });
}

function getGroupRowId(rows: any[]): string {
   const first = rows[0];

   return (
      asString(first?.ReservationId) ||
      asString(first?.ConfirmationNumber) ||
      getRowId(first)
   );
}

function flattenRows(reportJson: any): ReportRowVm[] {
   const groups = groupRowsByReservation(getGuestCreditLimitRows(reportJson));

   return groups.map((groupRows) => {
      const first = groupRows[0];
      const totalBalance = getTotalPositiveBalance(groupRows);
      const summaryLimit = getSummaryLimit(groupRows);
      const summaryVariance = getSummaryVariance(groupRows);

      return {
         rowId: `guest-credit-limit:${getGroupRowId(groupRows)}`,
         cells: {
            rowType: 'guestCreditLimit',
            room: asString(first?.RoomNumber),
            guestName: asString(first?.Name),
            noPost: asString(first?.NoPost),
            paymentWindows: formatWindowPaymentWithBalance(groupRows),
            payee: formatWindowPayees(groupRows),
            limitAndAuth: formatCurrency(summaryLimit),
            limitAndAuthRaw: summaryLimit,

            balance: formatCurrency(totalBalance),
            balanceRaw: totalBalance,

            variance: formatCurrency(summaryVariance),
            varianceRaw: summaryVariance,

            alert: getAlertDisplay(groupRows),
            alertRaw: getAlertFilterText(groupRows),
            alertSortRank: getAlertSortRank(groupRows),
            alertSeverity: getAlertSeverity(groupRows),
            reservationId: asString(first?.ReservationId),
            confirmationNumber: asString(first?.ConfirmationNumber)
         }
      };
   });
}

function getHeaderSnippets(reportJson: any): ReportHeaderSnippet[] {
   const groups = groupRowsByReservation(getGuestCreditLimitRows(reportJson));

   const riskCount = groups.filter((rows) => getRiskLabels(rows).length > 0).length;

   const totalExceeded = groups.reduce((total, rows) => {
      const variance = getSummaryVariance(rows);
      return variance < 0 ? total + variance : total;
   }, 0);

   return [
      {
         label: 'Risks',
         value: riskCount,
         tone: riskCount > 0 ? 'danger' : 'success'
      },
      {
         label: 'Total Exceeded',
         value: formatCurrency(totalExceeded),
         tone: totalExceeded < 0 ? 'danger' : 'success'
      }
   ];
}

export const GUEST_CREDIT_LIMIT_DEFINITION: ReportDefinition = {
   reportType: 'GuestCreditLimit',
   version: 5,
   title: 'Guest Credit Limit',

   sorting: {
      enabled: true,
      persist: true
   },

   rowSeverity: {
      enabled: true,
      getSeverity: (row) => row.cells['alertSeverity']
   },

   header: {
      title: 'Guest Credit Limit',
      snippets: ({ reportData }) => getHeaderSnippets(reportData)
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['guestCreditLimit']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['guestCreditLimit']
   },

   columns: [
      {
         key: 'room',
         header: 'ROOM',
         width: '76px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '220px',
         maxChars: 70,
         sort: { type: 'string' }
      },
      {
         key: 'noPost',
         header: 'POST',
         width: '70px',
         sort: { type: 'string' }
      },
      {
         key: 'paymentWindows',
         header: 'WIN: PAY MTH [BAL]',
         width: '180px',
         sort: { type: 'string' }
      },
      {
         key: 'payee',
         header: 'PAYEE',
         width: '280px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'limitAndAuth',
         header: 'LIMIT + AUTH',
         width: '130px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['limitAndAuthRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'balance',
         header: 'BALANCE',
         width: '90px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['balanceRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'variance',
         header: 'VARIANCE',
         width: '90px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['varianceRaw']
         }
      },
      {
         key: 'alert',
         header: 'ALERT',
         width: '210px',
         maxChars: 100,
         sort: {
            type: 'number',
            getValue: (row) => row.cells['alertSortRank']
         }
      }
   ],

   flattenRows,

   filter: {
      fields: [
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'guestName', label: 'Guest Name', type: 'string' },
         { key: 'noPost', label: 'No Post', type: 'string' },
         { key: 'paymentWindows', label: 'Payment Method', type: 'string' },
         { key: 'payee', label: 'Payee', type: 'string' },
         {
            key: 'alert',
            label: 'Alert',
            type: 'string',
            getValue: (row) => row.cells['alertRaw']
         },
         {
            key: 'balance',
            label: 'Balance',
            type: 'number',
            getValue: (row) => row.cells['balanceRaw']
         },
         {
            key: 'variance',
            label: 'Variance',
            type: 'number',
            getValue: (row) => row.cells['varianceRaw']
         }
      ]
   }
};
